import { ArrowUpRight, Mail } from 'lucide-react';
import { IMAGES, SITE } from '@/data';
import { Reveal } from './Reveal';
import { TikTokIcon } from './Mark';

const cellCls = 'rounded-3xl border border-ivory/10 bg-plum-card transition-[border-color,box-shadow] duration-300 hover:border-gold/40 hover:shadow-[0_16px_44px_rgba(0,0,0,0.35)]';

export default function AboutBento() {
  const socials = SITE.socials.filter((s) => s.url);

  return (
    <section id="about" className="mx-auto max-w-[1280px] scroll-mt-24 px-6 py-24 md:py-32" data-testid="about-section">
      <Reveal>
        <div className="mb-12">
          <span className="font-mono text-[13px] font-bold uppercase tracking-[0.25em] text-gold">The crew</span>
          <h2 className="mt-3 font-display text-5xl uppercase leading-[0.95] text-ivory md:text-7xl">
            IN THE <span className="text-mauve">DRIVER'S SEAT</span>
          </h2>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-5">
        {/* Photo cell — spans both rows */}
        <Reveal className="md:col-span-5 md:row-span-2">
          <div className={`relative min-h-[380px] h-full overflow-hidden ${cellCls}`} data-testid="about-photo-cell">
            <img
              src="/assets/nyah-live-purple.webp"
              alt="Princess Nyah performing live on stage"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-plum-deep via-plum-deep/20 to-transparent" aria-hidden="true" />
            <div className="absolute bottom-0 left-0 p-7">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold">Host &amp; curator</div>
              <div className="mt-1 font-display text-3xl uppercase tracking-wide text-ivory md:text-4xl">Princess Nyah</div>
            </div>
          </div>
        </Reveal>

        {/* Bio cell */}
        <Reveal delay={0.08} className="md:col-span-7">
          <div className={`flex h-full flex-col items-start gap-4 p-8 md:p-10 ${cellCls}`} data-testid="about-bio-cell">
            <span className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-gold">In the driver's seat</span>
            <h3 className="font-display text-4xl uppercase leading-none tracking-wide text-ivory md:text-5xl">
              PRINCESS <span className="text-mauve">NYAH</span>
            </h3>
            <p className="max-w-[520px] text-lg leading-relaxed text-ivory/75">{SITE.aboutBio}</p>
            <div className="mt-2 flex flex-wrap gap-3" data-testid="about-socials">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`about-social-${s.label.toLowerCase()}`}
                  className="inline-flex items-center gap-2 rounded-full border border-ivory/20 px-5 py-2.5 text-sm font-bold text-ivory transition-[border-color,color] duration-200 hover:border-mauve hover:text-mauve"
                >
                  {s.label === 'TikTok' && <TikTokIcon size={15} />}
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Booking cell — the money cell, gold so it pops */}
        <Reveal delay={0.14} className="md:col-span-7" data-testid="booking-cell">
          <div id="booking" className="flex h-full scroll-mt-24 flex-col items-start gap-4 rounded-3xl bg-gold p-8 text-plum shadow-[0_0_60px_rgba(232,184,106,0.3)]">
            <span className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-plum/70">Booking</span>
            <h3 className="font-display text-3xl uppercase leading-none tracking-wide text-plum md:text-4xl">
              BOOK <span className="text-ivory">PRINCESS NYAH</span>
            </h3>
            <a
              href="/booking.html"
              data-testid="booking-form-btn"
              className="mt-auto inline-flex items-center gap-2.5 rounded-full bg-plum px-6 py-3.5 font-extrabold text-gold transition-transform duration-200 hover:scale-[1.04] hover:shadow-[0_0_30px_rgba(0,0,0,0.3)]"
            >
              <ArrowUpRight size={16} aria-hidden="true" />
              Start a booking
            </a>
            {SITE.bookingEmail ? (
              <a
                href={`mailto:${SITE.bookingEmail}?subject=${encodeURIComponent('Booking enquiry: Princess Nyah')}`}
                data-testid="booking-email-link"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-plum/70 underline decoration-plum/30 underline-offset-4 transition-colors hover:text-plum"
              >
                <Mail size={13} aria-hidden="true" />
                or email {SITE.bookingEmail}
              </a>
            ) : null}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
